import React from 'react';
import { Card, Row, Col, Statistic } from 'antd';
import { DollarOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrencyBRL = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(toNumber(value));
};

const getPercent = (current, previous) => {
  if (previous === 0 || previous === undefined || previous === null) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
};

const getTrendIcon = (percent) => {
  if (percent === null) return null;
  if (percent > 0) return <ArrowUpOutlined style={{ color: '#3f8600' }} />;
  if (percent < 0) return <ArrowDownOutlined style={{ color: '#cf1322' }} />;
  return null;
};

const KPICardsV2 = ({ fluxoCaixa, historico }) => {
  const receitaMes = toNumber(fluxoCaixa?.entradas);
  const despesaMes = toNumber(fluxoCaixa?.saidas);
  const saldoMes = toNumber(fluxoCaixa?.saldo);
  const ebitdaMes = toNumber(fluxoCaixa?.ebitda);
  const margemEbitdaMes = toNumber(fluxoCaixa?.margem_ebitda);

  const receita12Meses = (historico || []).reduce((acc, item) => acc + toNumber(item?.receitas), 0);
  const despesa12Meses = (historico || []).reduce((acc, item) => acc + toNumber(item?.despesas), 0);

  const receitaAnterior = historico?.[historico.length - 2]?.receitas;
  const despesaAnterior = historico?.[historico.length - 2]?.despesas;
  const saldoAnterior = historico?.[historico.length - 2]?.saldo;

  const receitaPercent = getPercent(receitaMes, toNumber(receitaAnterior));
  const despesaPercent = getPercent(despesaMes, toNumber(despesaAnterior));
  const saldoPercent = getPercent(saldoMes, toNumber(saldoAnterior));

  const receitaMedia = historico && historico.length > 0
    ? receita12Meses / historico.length
    : 0;
  const despesaMedia = historico && historico.length > 0
    ? despesa12Meses / historico.length
    : 0;

  const formatPercent = (percent) => {
    if (percent === null) return '—';
    return `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`;
  };

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
      <Col xs={24} sm={12} xl={4}>
        <Card>
          <Statistic
            title="Receita do Mês"
            value={receitaMes}
            precision={2}
            prefix={<DollarOutlined />}
            valueStyle={{ color: '#3f8600' }}
            formatter={formatCurrencyBRL}
          />
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            {getTrendIcon(receitaPercent)} {formatPercent(receitaPercent)} vs mês anterior<br />
            Média 12m: {formatCurrencyBRL(receitaMedia)}
          </div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={4}>
        <Card>
          <Statistic
            title="Despesa do Mês"
            value={despesaMes}
            precision={2}
            prefix={<DollarOutlined />}
            valueStyle={{ color: '#cf1322' }}
            formatter={formatCurrencyBRL}
          />
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            {getTrendIcon(despesaPercent)} {formatPercent(despesaPercent)} vs mês anterior<br />
            Média 12m: {formatCurrencyBRL(despesaMedia)}
          </div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={4}>
        <Card>
          <Statistic
            title="Saldo do Mês"
            value={saldoMes}
            precision={2}
            prefix={saldoMes >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            valueStyle={{ color: saldoMes >= 0 ? '#3f8600' : '#cf1322' }}
            formatter={formatCurrencyBRL}
          />
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            {getTrendIcon(saldoPercent)} {formatPercent(saldoPercent)} vs mês anterior
          </div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={4}>
        <Card>
          <Statistic
            title="EBITDA"
            value={ebitdaMes}
            precision={2}
            prefix={ebitdaMes >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            valueStyle={{ color: ebitdaMes >= 0 ? '#3f8600' : '#cf1322' }}
            formatter={formatCurrencyBRL}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={4}>
        <Card>
          <Statistic
            title="Margem EBITDA (%)"
            value={margemEbitdaMes}
            precision={1}
            suffix="%"
            valueStyle={{ color: margemEbitdaMes >= 0 ? '#3f8600' : '#cf1322' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={4}>
        <Card>
          <Statistic
            title="Receita Acumulada 12 Meses"
            value={receita12Meses}
            precision={2}
            prefix={<ArrowUpOutlined />}
            valueStyle={{ color: '#3f8600' }}
            formatter={formatCurrencyBRL}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={4}>
        <Card>
          <Statistic
            title="Despesa Acumulada 12 Meses"
            value={despesa12Meses}
            precision={2}
            prefix={<ArrowDownOutlined />}
            valueStyle={{ color: '#cf1322' }}
            formatter={formatCurrencyBRL}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default KPICardsV2;
